"use client";

import { useState, useEffect, useCallback, useRef } from 'react';
import Pagination from '../components/Pagination';
import {
  Plus,
  Edit,
  Trash2,
  Eye,
  Search,
  RefreshCw,
  Users,
  Calendar as CalendarIcon,
  Clock,
  CheckCircle,
  XCircle,
  Pause,
  Play,
  X,
  Check,
  AlertCircle,
  AlertTriangle,
  FileText,
  BarChart3,
  TrendingUp,
  Filter,
  Download,
  Upload,
  Settings,
  UserCheck,
  Target,
  Activity,
  ArrowUpDown,
  Utensils,
  ChevronDown,
  ChevronUp,
  PieChart,
  Info,
  Printer
} from 'lucide-react';
import {
  APIURL,
  AdminGetAllDietCharts,
  AdminGetAllDietChartsList,
  AdminGetActiveDietCharts,
  AdminGetDietChartById,
  AdminCreateDietChart,
  AdminUpdateDietChart,
  AdminDeleteDietChart,
  AdminActivateDietChart,
  AdminDeactivateDietChart,
  AdminBulkUploadDietCharts,
  AdminExportDietCharts,
  AdminAssignDietChartGlobally,
  AdminAssignDietChartSelectively,
  AdminAssignDietChartBySubscriptionPlan,
  AdminAssignDietChartByAddonPlan,
  AdminGetUserDietChartAssignments,
  AdminGetUserActiveDietChartAssignments,
  AdminGetDietChartAssignments,
  AdminGetGlobalDietChartAssignments,
  AdminGetSelectiveDietChartAssignments,
  AdminUpdateAssignmentProgress,
  AdminCompleteAssignment,
  AdminCancelAssignment,
  AdminSuspendAssignment,
  AdminReactivateAssignment,
  AdminGetAssignmentStatistics,
  AdminGetAssignmentsNeedingRenewal,
  AdminGetExpiringAssignments,
  AdminGetAssignmentsByAdmin,
  AdminGetAssignmentsByPriority,
  AdminGetHighCompletionAssignments,
  Getsubscriptionplans,
  AdminGetAllAddonPlans,
  AdminGetAllFoodMacros
} from '../components/Api';

const SearchableSelect = ({ value, onChange, options, placeholder, fallbackLabel, emptyText = 'No options found', disabled = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredOptions = options.filter(opt =>
    opt.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedOption = options.find(opt => String(opt.value) === String(value));
  if (value) {
    console.log("SearchableSelect matching:", { value, matched: selectedOption, optionsSample: options.slice(0, 2) });
  }

  return (
    <div ref={wrapperRef} className={`relative w-full ${isOpen ? 'z-[100]' : 'z-10'}`}>
      <div
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white text-black cursor-pointer flex justify-between items-center"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span
          className={selectedOption || fallbackLabel ? "font-medium truncate" : "truncate"}
          style={{ color: selectedOption || fallbackLabel ? '#111827' : '#6b7280' }}
        >
          {selectedOption ? selectedOption.label : fallbackLabel || placeholder}
        </span>
        <ChevronDown className="w-4 h-4 text-gray-500 flex-shrink-0 ml-2" />
      </div>

      {isOpen && (
        <div className="absolute z-[100] w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-60 flex flex-col">
          <div className="p-2 border-b border-gray-100 sticky top-0 bg-white z-[101]">
            <input
              type="text"
              className="w-full px-3 py-1.5 bg-gray-50 border border-gray-300 rounded-md text-sm text-gray-900 placeholder-gray-500 outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Search food..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              autoFocus
            />
          </div>
          <div className="overflow-y-auto">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((opt) => (
                <div
                  key={opt.value}
                  className={`px-3 py-2 text-sm cursor-pointer hover:bg-blue-50 truncate ${String(value) === String(opt.value) ? 'bg-blue-100 text-blue-800 font-medium' : 'text-gray-900'}`}
                  onClick={() => {
                    onChange(opt.value);
                    setIsOpen(false);
                    setSearchTerm('');
                  }}
                >
                  {opt.label}
                </div>
              ))
            ) : (
              <div className="px-3 py-2 text-sm text-gray-500 text-center">No results found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default function DietChartsPage() {
  const [dietCharts, setDietCharts] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDietChart, setSelectedDietChart] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [refreshing, setRefreshing] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ show: false, title: '', message: '', onConfirm: null });
  const [formErrors, setFormErrors] = useState({});

  // New state for enhanced features
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize, setPageSize] = useState(6);
  const [statistics, setStatistics] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [showStatistics, setShowStatistics] = useState(false);
  const [showAssignments, setShowAssignments] = useState(false);

  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [showBulkInstructions, setShowBulkInstructions] = useState(false);

  // Plan management state
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [addonPlans, setAddonPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [allMacros, setAllMacros] = useState([]);
  const [macrosLoading, setMacrosLoading] = useState(false);

  // Auth header helper
  const getAuthHeaders = () => {
    const token = localStorage.getItem('accessToken');
    const tokenType = localStorage.getItem('tokenType') || 'Bearer';
    return {
      'Authorization': token ? `${tokenType} ${token}` : '',
      'Content-Type': 'application/json'
    };
  };

  // Assignment modal state
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignMode, setAssignMode] = useState('global'); // 'global' | 'selective' | 'subscription' | 'addon'
  const [assignDietChartId, setAssignDietChartId] = useState(null);
  const [assignedBy, setAssignedBy] = useState('admin');
  const [assignReason, setAssignReason] = useState('');
  const [planType, setPlanType] = useState('subscription'); // 'subscription' | 'addon'
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [selectedSubscriptionPlanId, setSelectedSubscriptionPlanId] = useState('');
  const [selectedAddonPlanId, setSelectedAddonPlanId] = useState('');

  // Form data
  const [formData, setFormData] = useState({
    dietChartName: '',
    targetGoal: '',
    caloriesPerDay: '',
    proteinGramsPerDay: '',
    carbsGramsPerDay: '',
    fatsGramsPerDay: '',
    mealsPerDay: 3,
    mealPlan: [],
    duration: '',
    price: '',
    imageUrl: '',
    description: '',
    isActive: true,
    category: '',
    tags: '',
    dietType: 'VEGETARIAN',
    showMacros: false
  });

  // Meal plan management state - Structured approach
  const [mealPlanEntries, setMealPlanEntries] = useState([
    {
      id: Date.now(),
      mealType: 'Breakfast',
      customMealType: '', // For storing custom meal type name
      items: []
    }
  ]);

  const [currentTotals, setCurrentTotals] = useState({
    calories: 0,
    protein: 0,
    carbs: 0,
    fat: 0
  });

  // Calculate real-time totals whenever mealPlanEntries change
  useEffect(() => {
    let totals = { calories: 0, protein: 0, carbs: 0, fat: 0 };
    mealPlanEntries.forEach(meal => {
      meal.items.forEach(item => {
        totals.calories += Number(item.caloriesInKcal || 0);
        totals.protein += Number(item.proteinInGrams || 0);
        totals.carbs += Number(item.carbsInGrams || 0);
        totals.fat += Number(item.fatInGrams || 0);
      });
    });
    // Round to 2 decimal places
    Object.keys(totals).forEach(key => {
      totals[key] = Math.round(totals[key] * 100) / 100;
    });
    setCurrentTotals(totals);
  }, [mealPlanEntries]);

  // Status filter options for active/inactive diet charts
  const statusOptions = [
    { label: 'Active', value: 'ACTIVE' },
    { label: 'Inactive', value: 'INACTIVE' }
  ];

  const categoryOptions = [
    { label: 'Fat Loss', value: 'FAT_LOSS' },
    { label: 'Muscle Building', value: 'MUSCLE_BUILDING' }
  ];

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
  };

  // Meal plan management functions
  const fetchAllMacros = async () => {
    setMacrosLoading(true);
    try {
      const response = await AdminGetAllFoodMacros(0, 1000); // Fetch a large enough batch
      if (response.data && response.data.content) {
        setAllMacros(response.data.content);
      } else if (Array.isArray(response.data)) {
        setAllMacros(response.data);
      }
    } catch (error) {
      console.error('Error fetching macros:', error);
    } finally {
      setMacrosLoading(false);
    }
  };

  const addMealPlanEntry = () => {
    setMealPlanEntries([...mealPlanEntries, {
      id: Date.now(),
      mealType: '',
      customMealType: '',
      items: []
    }]);
  };

  const removeMealPlanEntry = (mealId) => {
    if (mealPlanEntries.length > 1) {
      setMealPlanEntries(mealPlanEntries.filter(m => m.id !== mealId));
    }
  };

  const updateMealType = (mealId, newType) => {
    setMealPlanEntries(mealPlanEntries.map(m =>
      m.id === mealId ? { ...m, mealType: newType, customMealType: newType === 'Custom' ? m.customMealType : '' } : m
    ));
  };

  const updateCustomMealType = (mealId, customName) => {
    setMealPlanEntries(mealPlanEntries.map(m =>
      m.id === mealId ? { ...m, customMealType: customName } : m
    ));
  };

  const addMealItem = (mealId) => {
    setMealPlanEntries(mealPlanEntries.map(m => {
      if (m.id === mealId) {
        return {
          ...m,
          items: [...m.items, {
            id: Date.now() + Math.random(),
            foodMacroValue: '',
            food: '',
            qty: '',
            unit: '',
            caloriesInKcal: 0,
            proteinInGrams: 0,
            carbsInGrams: 0,
            fatInGrams: 0,
            remarks: ''
          }]
        };
      }
      return m;
    }));
  };

  const removeMealItem = (mealId, itemId) => {
    setMealPlanEntries(mealPlanEntries.map(m => {
      if (m.id === mealId) {
        return {
          ...m,
          items: m.items.filter(item => item.id !== itemId)
        };
      }
      return m;
    }));
  };

  const updateMealItem = (mealId, itemId, field, value) => {
    setMealPlanEntries(mealPlanEntries.map(m => {
      if (m.id === mealId) {
        const newItems = m.items.map(item => {
          if (item.id === itemId) {
            let updatedItem = { ...item, [field]: value };

            // If food item is selected, automatically populate quantity and calculate nutritional values
            if (field === 'foodMacroValue') {
              const macro = allMacros.find(m => String(m.id) === String(value));
              if (macro) {
                // Set the default quantity from macro data
                updatedItem.qty = macro.qty || 100;
                updatedItem.unit = macro.unit;
                updatedItem.food = macro.food;

                // Calculate nutritional values based on default quantity
                const ratio = Number(updatedItem.qty) / (macro.qty || 1);
                updatedItem.caloriesInKcal = Math.round(macro.caloriesInKcal * ratio * 100) / 100;
                updatedItem.proteinInGrams = Math.round(macro.proteinInGrams * ratio * 100) / 100;
                updatedItem.carbsInGrams = Math.round(macro.carbsInGrams * ratio * 100) / 100;
                updatedItem.fatInGrams = Math.round(macro.fatInGrams * ratio * 100) / 100;
              }
            }
            // If quantity changed, recalculate nutritional values
            else if (field === 'qty') {
              const macroId = item.foodMacroValue;
              if (macroId && value) {
                const macro = allMacros.find(m => String(m.id) === String(macroId));
                if (macro) {
                  const ratio = Number(value) / (macro.qty || 1);
                  updatedItem.caloriesInKcal = Math.round(macro.caloriesInKcal * ratio * 100) / 100;
                  updatedItem.proteinInGrams = Math.round(macro.proteinInGrams * ratio * 100) / 100;
                  updatedItem.carbsInGrams = Math.round(macro.carbsInGrams * ratio * 100) / 100;
                  updatedItem.fatInGrams = Math.round(macro.fatInGrams * ratio * 100) / 100;
                }
              }
            }
            return updatedItem;
          }
          return item;
        });
        return { ...m, items: newItems };
      }
      return m;
    }));
  };

  const convertMealPlanToObject = () => {
    const flatList = [];
    mealPlanEntries.forEach(meal => {
      // Use custom meal type if Custom is selected and customMealType is provided
      const finalMealType = meal.mealType === 'Custom' && meal.customMealType ? meal.customMealType : meal.mealType;

      meal.items.forEach(item => {
        flatList.push({
          mealType: finalMealType,
          food: item.food,
          qty: Number(item.qty),
          unit: item.unit,
          caloriesInKcal: Number(item.caloriesInKcal),
          carbsInGrams: Number(item.carbsInGrams),
          proteinInGrams: Number(item.proteinInGrams),
          fatInGrams: Number(item.fatInGrams),
          remarks: item.remarks,
          foodMacroValue: String(item.foodMacroValue)
        });
      });
    });
    return flatList;
  };

  const loadMealPlanFromObject = (mealPlanList) => {
    if (Array.isArray(mealPlanList) && mealPlanList.length > 0) {
      // Group by mealType
      const groups = {};
      const predefinedTypes = ['Breakfast', 'Lunch', 'Dinner', 'Snacks', 'Pre-Workout', 'Post-Workout'];

      mealPlanList.forEach((item, index) => {
        const type = item.mealType || 'General';
        if (!groups[type]) {
          // Check if it's a custom type
          const isCustom = !predefinedTypes.includes(type);
          groups[type] = {
            id: index,
            mealType: isCustom ? 'Custom' : type,
            customMealType: isCustom ? type : '',
            items: []
          };
        }
        groups[type].items.push({
          ...item,
          id: Date.now() + index + Math.random()
        });
      });
      setMealPlanEntries(Object.values(groups));
    } else {
      setMealPlanEntries([
        { id: Date.now(), mealType: 'Breakfast', customMealType: '', items: [] }
      ]);
    }
  };

  // Assignment functions
  const handleAssignDietChart = async () => {
    const activePlanId = assignMode === 'subscription' ? selectedSubscriptionPlanId : selectedAddonPlanId;
    if ((assignMode === 'subscription' || assignMode === 'addon') && !activePlanId) {
      showToast('Please select a plan to assign', 'error');
      return;
    }
    if (assignMode === 'selective' && (!selectedUserIds || selectedUserIds.length === 0)) {
      showToast('Please select at least one user to assign', 'error');
      return;
    }

    try {
      let response;

      switch (assignMode) {
        case 'global':
          response = await AdminAssignDietChartGlobally(assignDietChartId, assignedBy, assignReason);
          break;
        case 'selective':
          response = await AdminAssignDietChartSelectively(assignDietChartId, selectedUserIds, assignedBy, assignReason);
          break;
        case 'subscription':
          response = await AdminAssignDietChartBySubscriptionPlan(assignDietChartId, activePlanId, assignedBy, assignReason);
          break;
        case 'addon':
          response = await AdminAssignDietChartByAddonPlan(assignDietChartId, activePlanId, assignedBy, assignReason);
          break;
        default:
          throw new Error('Invalid assignment mode');
      }

      showToast(`Diet chart assigned successfully!`, 'success');
      setShowAssignModal(false);
      resetAssignmentForm();

      // Refresh assignments if showing
      if (showAssignments) {
        fetchAssignments();
      }
    } catch (error) {
      console.error('Error assigning diet chart:', error);
      showToast('Error assigning diet chart', 'error');
    }
  };

  const resetAssignmentForm = () => {
    setAssignMode('global');
    setAssignDietChartId(null);
    setAssignedBy('admin');
    setAssignReason('');
    setSelectedUserIds([]);
    setSelectedSubscriptionPlanId('');
    setSelectedAddonPlanId('');
  };

  const openAssignModal = (dietChartId) => {
    setAssignDietChartId(dietChartId);
    setAssignMode('global');
    setAssignedBy('admin');
    setAssignReason('');
    setSelectedUserIds([]);
    setSelectedSubscriptionPlanId('');
    setSelectedAddonPlanId('');
    setShowAssignModal(true);
  };

  const openAssignGlobal = (dietChartId) => {
    setAssignDietChartId(dietChartId);
    setAssignMode('global');
    setAssignedBy('admin');
    setAssignReason('');
    setSelectedUserIds([]);
    setSelectedSubscriptionPlanId('');
    setSelectedAddonPlanId('');
    setShowAssignModal(true);
  };

  const openAssignByPlan = (dietChartId) => {
    setAssignDietChartId(dietChartId);
    setAssignMode('subscription');
    setAssignedBy('admin');
    setAssignReason('');
    setSelectedUserIds([]);
    setSelectedSubscriptionPlanId('');
    setSelectedAddonPlanId('');
    setShowAssignModal(true);
  };

  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  // Debounce search term
  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(timerId);
  }, [searchTerm]);

  const fetchDietCharts = useCallback(async (page = currentPage, size = pageSize, isRefresh = false, search = debouncedSearchTerm, status = filterStatus) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      const response = await AdminGetAllDietCharts(page, size, search, status);
      const data = response.data;

      if (data && data.content) {
        setDietCharts(data.content);
        setTotalPages(data.totalPages);
        setTotalElements(data.totalElements);
        setCurrentPage(data.number);
      } else if (Array.isArray(data)) {
        setDietCharts(data);
        setTotalPages(1);
        setTotalElements(data.length);
        setCurrentPage(0);
      } else {
        setDietCharts([]);
        setTotalPages(0);
        setTotalElements(0);
      }
    } catch (error) {
      console.error('Error fetching diet charts:', error);
      showToast('Error fetching diet charts', 'error');
      setDietCharts([]);
    } finally {
      if (isRefresh) {
        setRefreshing(false);
      } else {
        setLoading(false);
      }
    }
  }, [currentPage, pageSize, debouncedSearchTerm, filterStatus]);

  const handleRefresh = () => {
    fetchDietCharts(currentPage, pageSize, true, debouncedSearchTerm, filterStatus);
  };

  useEffect(() => {
    fetchDietCharts(0, pageSize, false, debouncedSearchTerm, filterStatus);
  }, [debouncedSearchTerm, filterStatus, pageSize]); // Re-fetch when search, status, or page size changes

  useEffect(() => {
    // We already fetch in the effect above, so only fetch when explicitly needed or on mount components that don't depend on search
    fetchUsers();
    fetchPlans();
    fetchAllMacros();
  }, []);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${APIURL}/api/v1/users/getAll`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        const list = Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
            ? data.data
            : Array.isArray(data?.content)
              ? data.content
              : [];
        const options = list.map(user => ({
          label: `${user.firstName ?? ''} ${user.lastName ?? ''} (${user.email ?? ''})`.trim(),
          value: user.userId ?? user.id ?? user.uid ?? '',
          userId: user.userId ?? user.id ?? user.uid ?? '',
          firstName: user.firstName ?? '',
          lastName: user.lastName ?? '',
          email: user.email ?? ''
        }));
        setUsers(options);
      } else {
        setUsers([]);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsers([]);
    }
  };

  const fetchPlans = async () => {
    setPlansLoading(true);
    try {
      // Fetch subscription plans
      const subscriptionResponse = await Getsubscriptionplans();

      if (subscriptionResponse.data) {
        // Handle SuccessResponseDTO structure: { message, data, status, timestamp }
        let plans = [];
        if (subscriptionResponse.data.data) {
          // Data is wrapped in SuccessResponseDTO
          plans = Array.isArray(subscriptionResponse.data.data)
            ? subscriptionResponse.data.data
            : [];
        } else if (Array.isArray(subscriptionResponse.data)) {
          // Direct array response
          plans = subscriptionResponse.data;
        } else if (subscriptionResponse.data.content) {
          // Paginated response
          plans = subscriptionResponse.data.content;
        }
        setSubscriptionPlans(plans);
      }

      // Fetch addon plans
      const addonResponse = await AdminGetAllAddonPlans();

      if (addonResponse.data) {
        // Handle SuccessResponseDTO structure: { message, data, status, timestamp }
        let plans = [];
        if (addonResponse.data.data) {
          // Data is wrapped in SuccessResponseDTO
          plans = Array.isArray(addonResponse.data.data)
            ? addonResponse.data.data
            : [];
        } else if (Array.isArray(addonResponse.data)) {
          // Direct array response
          plans = addonResponse.data;
        } else if (addonResponse.data.content) {
          // Paginated response
          plans = addonResponse.data.content;
        }
        setAddonPlans(plans);
      }
    } catch (error) {
      console.error('Error fetching plans:', error);
      setSubscriptionPlans([]);
      setAddonPlans([]);
      showToast('Error loading plans', 'error');
    } finally {
      setPlansLoading(false);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.dietChartName) errors.dietChartName = 'Diet chart name is required';

    if (!formData.caloriesPerDay) errors.caloriesPerDay = 'Calories per day is required';
    if (!formData.dietType) errors.dietType = 'Diet type is required';
    // Price is now optional - no validation needed

    if (!formData.mealsPerDay) errors.mealsPerDay = 'Meals per day is required';

    // Validate meal plan entries - each meal must have a meal type
    const mealTypeErrors = [];
    mealPlanEntries.forEach((meal, index) => {
      if (!meal.mealType || meal.mealType.trim() === '') {
        mealTypeErrors.push(`Meal group ${index + 1} must have a meal type`);
      } else if (meal.mealType === 'Custom' && (!meal.customMealType || meal.customMealType.trim() === '')) {
        mealTypeErrors.push(`Meal group ${index + 1}: Please enter a custom meal type name`);
      }
    });
    if (mealTypeErrors.length > 0) {
      errors.mealPlan = mealTypeErrors.join(', ');
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreate = async () => {
    if (!validateForm()) return;
    try {
      const mealPlanObj = convertMealPlanToObject();
      // Send null for price if empty, otherwise send the value
      const priceValue = formData.price === '' || formData.price === null || formData.price === undefined ? null : formData.price;
      const formDataWithMealPlan = { ...formData, price: priceValue, mealPlan: mealPlanObj };
      await AdminCreateDietChart(formDataWithMealPlan);
      showToast(`Diet chart "${formData.dietChartName}" created successfully`, 'success');
      setShowCreateDialog(false);
      resetForm();
      fetchDietCharts();
    } catch (error) {
      console.error('Error creating diet chart:', error);
      showToast('Error creating diet chart', 'error');
    }
  };

  const handleUpdate = async () => {
    if (!validateForm()) return;
    try {
      const mealPlanObj = convertMealPlanToObject();
      // Send null for price if empty, otherwise send the value
      const priceValue = formData.price === '' || formData.price === null || formData.price === undefined ? null : formData.price;
      // If imageUrl is empty, keep the old one
      const updatedData = {
        ...formData,
        price: priceValue,
        mealPlan: mealPlanObj,
        imageUrl: formData.imageUrl ? formData.imageUrl : selectedDietChart.imageUrl
      };

      await AdminUpdateDietChart(selectedDietChart.id, updatedData);
      showToast(`Diet chart "${formData.dietChartName}" updated successfully`, 'success');
      setShowEditDialog(false);
      resetForm();
      fetchDietCharts();
    } catch (error) {
      console.error('Error updating diet chart:', error);
      showToast('Error updating diet chart', 'error');
    }
  };

  const handleDelete = (id) => {
    setConfirmModal({
      show: true,
      title: 'Confirm Delete',
      message: 'Are you sure you want to delete this diet chart? This action cannot be undone.',
      onConfirm: async () => {
        try {
          await AdminDeleteDietChart(id);
          showToast('Diet chart deleted successfully');
          fetchDietCharts();
        } catch (error) {
          console.error('Error deleting diet chart:', error);
          showToast('Error deleting diet chart', 'error');
        } finally {
          setConfirmModal(prev => ({ ...prev, show: false }));
        }
      }
    });
  };

  // New enhanced functions
  const handleActivate = async (id) => {
    try {
      await AdminActivateDietChart(id);
      showToast('Diet chart activated successfully');
      fetchDietCharts();
    } catch (error) {
      console.error('Error activating diet chart:', error);
      showToast('Error activating diet chart', 'error');
    }
  };

  const handleDeactivate = async (id) => {
    try {
      await AdminDeactivateDietChart(id);
      showToast('Diet chart deactivated successfully');
      fetchDietCharts();
    } catch (error) {
      console.error('Error deactivating diet chart:', error);
      showToast('Error deactivating diet chart', 'error');
    }
  };

  // const fetchStatistics = async () => {
  //   try {
  //     const response = await AdminGetAssignmentStatistics();
  //     setStatistics(response.data || {});
  //   } catch (error) {
  //     // Handle 404 as "no data" rather than an error
  //     if (error.response && error.response.status === 404) {
  //       setStatistics({});
  //       return;
  //     }
  //     console.error('Error fetching statistics:', error);
  //     showToast('Error fetching statistics', 'error');
  //     setStatistics({});
  //   }
  // };

  // const fetchAssignments = async () => {
  //   try {
  //     const response = await AdminGetGlobalDietChartAssignments();
  //     setAssignments(response.data || []);
  //     console.log('Assignments fetched:', response.data);
  //   } catch (error) {
  //     // Handle 404 as "no data" rather than an error
  //     if (error.response && error.response.status === 404) {
  //       setAssignments([]);
  //       return;
  //     }
  //     console.error('Error fetching assignments:', error);
  //     showToast('Error fetching assignments', 'error');
  //     setAssignments([]);
  //   }
  // };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    fetchDietCharts(newPage, pageSize);
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(0);
    fetchDietCharts(0, newSize);
  };


  const handleSuspend = async (id) => {
    try {
      console.log('Suspending assignment with ID:', id);
      await AdminSuspendAssignment(id, 'Suspended by admin');
      showToast('Diet chart assignment suspended successfully');
      // Always refresh assignments to show updated status
      fetchAssignments();
      console.log('Assignment suspended, refreshing data...');
    } catch (error) {
      console.error('Error suspending diet chart assignment:', error);
      showToast('Error suspending diet chart assignment', 'error');
    }
  };

  const handleReactivate = async (id) => {
    try {
      console.log('Reactivating assignment with ID:', id);
      await AdminReactivateAssignment(id);
      showToast('Diet chart assignment reactivated successfully');
      // Always refresh assignments to show updated status
      fetchAssignments();
      console.log('Assignment reactivated, refreshing data...');
    } catch (error) {
      console.error('Error reactivating diet chart assignment:', error);
      showToast('Error reactivating diet chart assignment', 'error');
    }
  };

  const handleComplete = async (id) => {
    try {
      console.log('Completing assignment with ID:', id);
      await AdminCompleteAssignment(id, 'Completed by admin');
      showToast('Diet chart assignment completed successfully');
      // Always refresh assignments to show updated status
      fetchAssignments();
      console.log('Assignment completed, refreshing data...');
    } catch (error) {
      console.error('Error completing diet chart assignment:', error);
      showToast('Error completing diet chart assignment', 'error');
    }
  };

  const handleCancel = async (id) => {
    try {
      console.log('Cancelling assignment with ID:', id);
      await AdminCancelAssignment(id, 'Cancelled by admin', 'admin');
      showToast('Diet chart assignment cancelled successfully');
      // Always refresh assignments to show updated status
      fetchAssignments();
      console.log('Assignment cancelled, refreshing data...');
    } catch (error) {
      console.error('Error cancelling diet chart assignment:', error);
      showToast('Error cancelling diet chart assignment', 'error');
    }
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setLoading(true);
    try {
      await AdminBulkUploadDietCharts(file);
      showToast('Diet charts uploaded successfully', 'success');
      fetchDietCharts();
    } catch (error) {
      console.error('Error uploading diet charts:', error);

      // Extract error message from response
      let errorMessage = 'Error uploading diet charts. Please check the file format.';

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data) {
        // If the error data is a string
        errorMessage = typeof error.response.data === 'string'
          ? error.response.data
          : error.response.data.error || errorMessage;
      } else if (error.message) {
        errorMessage = error.message;
      }

      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
      // Reset input
      if (e.target) e.target.value = '';
    }
  };

  const handleDownloadTemplate = () => {
    const headers = [
      'Diet Chart Name', 'Target Goal', 'Calories Per Day', 'Diet Type', 'Show Macros (TRUE/FALSE)',
      'Protein (g/day)', 'Carbs (g/day)', 'Fats (g/day)', 'Meals Per Day', 'Duration',
      'Meal Type', 'Food', 'Qty', 'Unit', 'Calories (item)',
      'Protein (item)', 'Carbs (item)', 'Fat (item)', 'Remarks (item)', 'Food Macro ID (optional)'
    ];

    const sampleRow = [
      'Muscle Gain Pro', 'Lean Muscle Building', '2800', 'NON_VEGETARIAN', 'TRUE',
      '180', '350', '80', '5', '90 Days',
      'Breakfast', 'Scrambled Eggs (6)', '150', 'g', '450',
      '36', '2', '32', 'High protein start', ''
    ];

    const csvContent = [
      headers.join(','),
      sampleRow.join(',')
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'diet_chart_template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadInstructions = () => {
    const content = `
DIET CHART BULK UPLOAD INSTRUCTIONS
====================================

Follow these guidelines to prepare your spreadsheet for bulk upload.

FILE FORMAT:
- Supported formats: .csv, .xlsx, .xls
- The system groups multiple rows into one Diet Chart based on the "Diet Chart Name".

COLUMN GUIDE & ACCEPTED VALUES:

1. Diet Chart Name (Required)
   - Must be the same for all rows belonging to the same diet chart.
   
2. Target Goal (Required)
   - e.g., Weight Loss, Muscle Building, Maintenance.

3. Calories Per Day (Required)
   - Numeric value (e.g., 2000).

4. Diet Type (Required)
   - Accepted: VEGETARIAN, NON_VEGETARIAN

5. Show Macros (Required)
   - Accepted: TRUE, FALSE

6. Protein/Carbs/Fats (g/day)
   - Numeric target values for the whole day.

7. Meals Per Day
   - Numeric value (1-10).

8. Duration
   - e.g., "30 Days", "12 Weeks".

9. Price
   - Numeric value. Leave empty if the chart is free.

10. Category
    - Accepted: WEIGHT_LOSS, WEIGHT_GAIN, MUSCLE_BUILDING, MAINTENANCE, ATHLETIC_PERFORMANCE

11. Meal Type
    - Accepted: Breakfast, Lunch, Dinner, Snacks, Pre-Workout, Post-Workout, or any Custom Name.

12. Food Item Details (Qty, Unit, Calories, Protein, etc.)
    - Nutritional values for individual food items.

13. Food Macro ID (Optional)
    - If provided, links the item to an existing entry in your Food Macros database.

TIPS:
- Ensure there are no empty rows in your file.
- Use capital letters for fixed options (VEGETARIAN, TRUE, etc.).
- Double-check that meal items belonging to the same chart have EXACTLY the same chart name.
`;
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'diet_chart_upload_instructions.txt');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const resetForm = () => {
    setFormData({
      dietChartName: '',
      targetGoal: '',
      caloriesPerDay: '',
      proteinGramsPerDay: '',
      carbsGramsPerDay: '',
      fatsGramsPerDay: '',
      mealsPerDay: 3,
      mealPlan: [],
      duration: '',
      price: '',
      imageUrl: '',
      description: '',
      isActive: true,
      category: '',
      tags: '',
      dietType: 'VEGETARIAN',
      showMacros: false
    });
    setMealPlanEntries([
      { id: Date.now(), mealType: 'Breakfast', customMealType: '', items: [] }
    ]);
    setFormErrors({});
  };




  const openEditDialog = (data) => {
    setSelectedDietChart(data);
    setFormData({
      dietChartName: data.dietChartName || '',
      targetGoal: data.targetGoal || '',
      caloriesPerDay: data.caloriesPerDay ?? '',
      proteinGramsPerDay: data.proteinGramsPerDay ?? '',
      carbsGramsPerDay: data.carbsGramsPerDay ?? '',
      fatsGramsPerDay: data.fatsGramsPerDay ?? '',
      mealsPerDay: data.mealsPerDay ?? '',
      mealPlan: data.mealPlan || [],
      duration: data.duration || '',
      price: data.price ?? '',
      imageUrl: '', // always empty for edit
      description: data.description || '',
      isActive: data.isActive ?? true,
      category: data.category || '',
      tags: data.tags || '',
      dietType: data.dietType || 'VEGETARIAN',
      showMacros: data.showMacros ?? false
    });
    loadMealPlanFromObject(data.mealPlan || []);
    setShowEditDialog(true);
  };

  const openViewDialog = (data) => {
    setSelectedDietChart(data);
    setShowViewDialog(true);
  };

  const handleExport = async () => {
    try {
      const response = await AdminExportDietCharts();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'diet_charts.xlsx');
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (error) {
      console.error('Error exporting diet charts:', error);
      showToast('Error exporting diet charts', 'error');
    }
  };

  const filteredData = (() => {
    let filtered = [...dietCharts];

    // Apply sorting
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aValue, bValue;

        if (sortConfig.key === 'dietChartName') {
          aValue = (a.dietChartName || '').toLowerCase();
          bValue = (b.dietChartName || '').toLowerCase();
        } else if (sortConfig.key === 'targetGoal') {
          aValue = (a.targetGoal || '').toLowerCase();
          bValue = (b.targetGoal || '').toLowerCase();
        } else if (sortConfig.key === 'caloriesPerDay') {
          aValue = Number(a.caloriesPerDay) || 0;
          bValue = Number(b.caloriesPerDay) || 0;
        } else if (sortConfig.key === 'duration') {
          aValue = (a.duration || '').toLowerCase();
          bValue = (b.duration || '').toLowerCase();
        } else if (sortConfig.key === 'price') {
          aValue = Number(a.price) || 0;
          bValue = Number(b.price) || 0;
        } else if (sortConfig.key === 'category') {
          aValue = (a.category || '').toLowerCase();
          bValue = (b.category || '').toLowerCase();
        } else if (sortConfig.key === 'status') {
          aValue = a.isActive ? 'active' : 'inactive';
          bValue = b.isActive ? 'active' : 'inactive';
        } else {
          aValue = a[sortConfig.key];
          bValue = b[sortConfig.key];
        }

        if (aValue == null) aValue = '';
        if (bValue == null) bValue = '';

        if (typeof aValue === 'string') aValue = aValue.toLowerCase();
        if (typeof bValue === 'string') bValue = bValue.toLowerCase();

        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  })();

  // Handle sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Get sort indicator
  const getSortIndicator = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === 'asc' ? '↑' : '↓';
    }
    return '';
  };

  return (
    <div className="p-4">
      {/* Toast Notification */}
      {toast.show && (
        <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg max-w-md ${toast.type === 'error' ? 'bg-red-500 text-white' : 'bg-green-500 text-white'
          }`}>
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              {toast.type === 'error' ? <AlertCircle className="w-5 h-5" /> : <Check className="w-5 h-5" />}
            </div>
            <div className="flex-1 text-sm leading-relaxed break-words">
              {toast.message}
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex flex-col lg:flex-row justify-between gap-6">
            <div className="flex-1">
              <div className="flex items-start gap-4">
                <div className="bg-green-100 p-3 rounded-lg">
                  <BarChart3 className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-gray-600 text-sm leading-relaxed max-w-3xl">
                    Manage diet charts and meal plans. Create, edit, and assign personalized nutrition plans to users based on their goals and preferences.
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="flex items-center gap-2 px-4 py-2.5 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white rounded-lg transition-all duration-200 disabled:cursor-not-allowed text-sm font-medium shadow-sm hover:shadow focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                aria-label={refreshing ? "Refreshing data" : "Refresh diet charts"}
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                <span>{refreshing ? 'Refreshing...' : 'Refresh'}</span>
              </button>
              <button
                onClick={() => {
                  resetForm();
                  setShowCreateDialog(true);
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg flex items-center gap-2 transition-colors text-sm font-medium shadow-sm hover:shadow focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                <Plus className="w-4 h-4" />
                New Diet Chart
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="bg-white rounded-lg shadow-sm border p-4 mb-4">
        <div className="flex justify-between items-center">
          <div className="flex gap-2 flex-wrap">

            <button
              onClick={handleExport}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Export Data
            </button>
            <div className="relative">
              <input
                type="file"
                id="bulk-upload-diet"
                className="hidden"
                accept=".csv, .xlsx, .xls"
                onChange={handleBulkUpload}
              />
            </div>
          </div>

          <div className="flex gap-2">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, goal, category..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black"
              />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black"
            >
              <option value="">All Status</option>
              {statusOptions.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>



      {/* Statistics Panel */}
      {showStatistics && statistics && (
        <div className="bg-white rounded-lg shadow-sm border p-4 mb-4">
          <h3 className="text-lg font-semibold mb-4 text-black">Assignment Statistics</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-center">
                <Activity className="w-8 h-8 text-blue-600" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-blue-600">Total Assignments</p>
                  <p className="text-2xl font-bold text-blue-900">{statistics.totalAssignments || 0}</p>
                </div>
              </div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="flex items-center">
                <CheckCircle className="w-8 h-8 text-green-600" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-green-600">Active Assignments</p>
                  <p className="text-2xl font-bold text-green-900">{statistics.activeAssignments || 0}</p>
                </div>
              </div>
            </div>
            <div className="bg-yellow-50 p-4 rounded-lg">
              <div className="flex items-center">
                <TrendingUp className="w-8 h-8 text-yellow-600" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-yellow-600">Completion Rate</p>
                  <p className="text-2xl font-bold text-yellow-900">{statistics.averageCompletionRate || 0}%</p>
                </div>
              </div>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg">
              <div className="flex items-center">
                <Users className="w-8 h-8 text-purple-600" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-purple-600">Users Assigned</p>
                  <p className="text-2xl font-bold text-purple-900">{statistics.uniqueUsersAssigned || 0}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Data Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    S.No
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('dietChartName')}
                  >
                    <div className="flex items-center">
                      Diet Chart Name
                      <ArrowUpDown className="ml-1 w-4 h-4" />
                      <span className="ml-1">{getSortIndicator('dietChartName')}</span>
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('dietType')}
                  >
                    <div className="flex items-center">
                      Diet Type
                      <ArrowUpDown className="ml-1 w-4 h-4" />
                      <span className="ml-1">{getSortIndicator('dietType')}</span>
                    </div>
                  </th>

                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('caloriesPerDay')}
                  >
                    <div className="flex items-center">
                      Calories/Day
                      <ArrowUpDown className="ml-1 w-4 h-4" />
                      <span className="ml-1">{getSortIndicator('caloriesPerDay')}</span>
                    </div>
                  </th>



                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('status')}
                  >
                    <div className="flex items-center">
                      Status
                      <ArrowUpDown className="ml-1 w-4 h-4" />
                      <span className="ml-1">{getSortIndicator('status')}</span>
                    </div>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredData.map((item, index) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{currentPage * pageSize + index + 1}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{item.dietChartName}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${item.dietType === 'NON_VEGETARIAN' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
                        {item.dietType === 'NON_VEGETARIAN' ? 'Non-Veg' : 'Veg'}
                      </span>
                    </td>

                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{item.caloriesPerDay}</td>


                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${item.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                        {item.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex gap-2">
                        <button
                          onClick={() => openViewDialog(item)}
                          className="text-blue-600 hover:text-blue-900"
                          title="View"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openEditDialog(item)}
                          className="text-yellow-600 hover:text-yellow-900"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        {item.isActive ? (
                          <button
                            onClick={() => handleDeactivate(item.id)}
                            className="text-orange-600 hover:text-orange-900"
                            title="Deactivate"
                          >
                            <Pause className="w-4 h-4" />
                          </button>
                        ) : (
                          <button
                            onClick={() => handleActivate(item.id)}
                            className="text-green-600 hover:text-green-900"
                            title="Activate"
                          >
                            <Play className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => openAssignByPlan(item.id)}
                          className="text-purple-600 hover:text-purple-900"
                          title="Assign by Plan"
                        >
                          <Users className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(item.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {filteredData.length === 0 && !loading && (
          <div className="p-8 text-center text-gray-500">
            <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p>No diet chart assignments found</p>
          </div>
        )}


      </div>
      {/* Pagination */}
      <Pagination
        currentPage={currentPage + 1}
        setCurrentPage={(page) => handlePageChange(page - 1)}
        totalPages={totalPages}
        totalElements={totalElements}
        pageSize={pageSize}
        setPageSize={handlePageSizeChange}
        itemName="Diet Charts"
      />

      {/* Create Dialog */}
      {showCreateDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-800">Create Diet Chart</h2>
              <button onClick={() => setShowCreateDialog(false)} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-800">Diet Chart Name *</label>
                  <input
                    type="text"
                    value={formData.dietChartName}
                    onChange={(e) => {
                      setFormData({ ...formData, dietChartName: e.target.value });
                      setFormErrors({ ...formErrors, dietChartName: '' });
                    }}
                    placeholder="Enter diet chart name"
                    className={`w-full px-3 py-2 border ${formErrors.dietChartName ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black`}
                  />
                  {formErrors.dietChartName && <p className="text-red-500 text-xs mt-1">{formErrors.dietChartName}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-800">Diet Type *</label>
                  <select
                    value={formData.dietType}
                    onChange={(e) => {
                      setFormData({ ...formData, dietType: e.target.value });
                      setFormErrors({ ...formErrors, dietType: '' });
                    }}
                    className={`w-full px-3 py-2 border ${formErrors.dietType ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black`}
                  >
                    <option value="VEGETARIAN">Vegetarian</option>
                    <option value="NON_VEGETARIAN">Non-Vegetarian</option>
                  </select>
                  {formErrors.dietType && <p className="text-red-500 text-xs mt-1">{formErrors.dietType}</p>}
                </div>

              </div>

              <div className="grid grid-cols-2 gap-4 pb-4 border-b border-gray-100">
                {/* <div>
                  <label className="block text-sm font-medium mb-2 text-gray-800">Diet Type *</label>
                  <select
                    value={formData.dietType}
                    onChange={(e) => {
                      setFormData({ ...formData, dietType: e.target.value });
                      setFormErrors({ ...formErrors, dietType: '' });
                    }}
                    className={`w-full px-3 py-2 border ${formErrors.dietType ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black`}
                  >
                    <option value="VEGETARIAN">Vegetarian</option>
                    <option value="NON_VEGETARIAN">Non-Vegetarian</option>
                  </select>
                  {formErrors.dietType && <p className="text-red-500 text-xs mt-1">{formErrors.dietType}</p>}
                </div> */}

                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-800">Calories/Day *</label>
                  <input
                    type="text"
                    value={formData.caloriesPerDay}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        caloriesPerDay: e.target.value === '' ? '' : parseInt(e.target.value) || '',
                      });
                      setFormErrors({ ...formErrors, caloriesPerDay: '' });
                    }}
                    placeholder="e.g., 2000"
                    className={`w-full px-3 py-2 border ${formErrors.caloriesPerDay ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black`}
                  />
                  {formErrors.caloriesPerDay && <p className="text-red-500 text-xs mt-1">{formErrors.caloriesPerDay}</p>}
                </div>

                <div className="flex items-center mt-6">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.showMacros}
                      onChange={(e) => setFormData({ ...formData, showMacros: e.target.checked })}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm font-medium text-gray-800">Enter macro nutrients</span>
                  </label>
                </div>
              </div>

              {formData.showMacros && (
                <div className="grid grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-800">Protein (g)</label>
                    <input
                      type="text"
                      value={formData.proteinGramsPerDay}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          proteinGramsPerDay: e.target.value === '' ? '' : parseInt(e.target.value) || '',
                        })
                      }
                      placeholder="e.g., 150"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-800">Carbs (g)</label>
                    <input
                      type="text"
                      value={formData.carbsGramsPerDay}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          carbsGramsPerDay: e.target.value === '' ? '' : parseInt(e.target.value) || '',
                        })
                      }
                      placeholder="e.g., 250"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-800">Fats (g)</label>
                    <input
                      type="text"
                      value={formData.fatsGramsPerDay}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          fatsGramsPerDay: e.target.value === '' ? '' : parseInt(e.target.value) || '',
                        })
                      }
                      placeholder="e.g., 250"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black"
                    />
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-800">Meals Per Day *</label>
                  <select
                    value={formData.mealsPerDay}
                    onChange={(e) => {
                      setFormData({ ...formData, mealsPerDay: parseInt(e.target.value) });
                      setFormErrors({ ...formErrors, mealsPerDay: '' });
                    }}
                    className={`w-full px-3 py-2 border ${formErrors.mealsPerDay ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black`}
                  >
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                      <option key={num} value={num}>{num} meal{num > 1 ? 's' : ''} per day</option>
                    ))}
                  </select>
                  {formErrors.mealsPerDay && <p className="text-red-500 text-xs mt-1">{formErrors.mealsPerDay}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-800">Duration</label>
                  <input
                    type="text"
                    value={formData.duration}
                    onChange={(e) => setFormData({ ...formData, duration: e.target.value === '' ? '' : parseInt(e.target.value) || '' })}
                    placeholder="e.g., 30 days"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-800">Price (Optional)</label>
                  <input
                    type="text"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        price: e.target.value === '' ? '' : parseFloat(e.target.value) || '',
                      });
                    }}
                    placeholder="e.g., 999.00 (leave empty if free)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black"
                  />
                </div>

              </div>



              {/* Meal Plan Section - Enhanced */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
                <div className="flex justify-between items-center mb-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Utensils className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Meal Plan Design</h3>
                      <p className="text-sm text-gray-600">Assign specific food items and quantities per meal</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={addMealPlanEntry}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center gap-2 shadow-sm hover:shadow-md"
                  >
                    <Plus className="w-4 h-4" />
                    Add Meal Group
                  </button>
                </div>

                <div className="space-y-6">
                  {mealPlanEntries.map((meal, mealIndex) => (
                    <div key={meal.id} className="bg-white rounded-xl border border-gray-200 shadow-sm">
                      <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex justify-between items-start rounded-t-xl">
                        <div className="flex-1 flex items-start gap-4">
                          <div className="flex-1">
                            <label className="block text-xs font-medium text-gray-600 mb-1">Meal Type *</label>
                            <div className="space-y-2">
                              <select
                                value={meal.mealType}
                                onChange={(e) => {
                                  updateMealType(meal.id, e.target.value);
                                  // Clear meal plan error when user selects a meal type
                                  if (formErrors.mealPlan) {
                                    setFormErrors({ ...formErrors, mealPlan: '' });
                                  }
                                }}
                                className={`w-full px-3 py-2 border ${!meal.mealType ? 'border-red-300 bg-red-50' : 'border-gray-300 bg-white'} rounded-lg text-gray-800 font-semibold text-base focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                              >
                                <option value="" disabled>Select meal type</option>
                                <option value="Breakfast">🍳 Breakfast</option>
                                <option value="Lunch">🍽️ Lunch</option>
                                <option value="Dinner">🌙 Dinner</option>
                                <option value="Snacks">🍿 Snacks</option>
                                <option value="Pre-Workout">💪 Pre-Workout</option>
                                <option value="Post-Workout">🏋️ Post-Workout</option>
                                <option value="Custom">✏️ Custom</option>
                              </select>

                              {/* Custom meal type input */}
                              {meal.mealType === 'Custom' && (
                                <input
                                  type="text"
                                  value={meal.customMealType}
                                  onChange={(e) => {
                                    updateCustomMealType(meal.id, e.target.value);
                                    // Clear meal plan error when user enters custom name
                                    if (formErrors.mealPlan) {
                                      setFormErrors({ ...formErrors, mealPlan: '' });
                                    }
                                  }}
                                  placeholder="Enter custom meal type name"
                                  className={`w-full px-3 py-2 border ${meal.mealType === 'Custom' && !meal.customMealType ? 'border-red-300 bg-red-50' : 'border-gray-300 bg-white'} rounded-lg text-gray-800 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                                />
                              )}
                            </div>
                            {!meal.mealType && (
                              <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                                <AlertCircle className="w-3 h-3" />
                                Please select a meal type
                              </p>
                            )}
                            {meal.mealType === 'Custom' && !meal.customMealType && (
                              <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                                <AlertCircle className="w-3 h-3" />
                                Please enter a custom meal type name
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-start gap-2 ml-4 pt-5">
                          <button
                            type="button"
                            onClick={() => addMealItem(meal.id)}
                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                            title="Add food item"
                          >
                            <Plus className="w-5 h-5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => removeMealPlanEntry(meal.id)}
                            className="p-1.5 text-red-500 hover:bg-red-50 rounded-md transition-colors"
                            title="Remove meal group"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </div>

                      <div className="p-4 space-y-3">
                        {meal.items.map((item) => (
                          <div key={item.id} className="grid grid-cols-12 gap-3 items-end bg-gray-50 p-3 rounded-lg border border-gray-100 relative group">
                            <div className="col-span-5">
                              <label className="block text-[10px] uppercase tracking-wider text-gray-400 font-bold mb-1">Food Item</label>
                              <SearchableSelect
                                value={item.foodMacroValue}
                                onChange={(val) => updateMealItem(meal.id, item.id, 'foodMacroValue', val)}
                                options={allMacros.map(m => ({ value: m.id, label: `${m.food} (${m.qty} ${m.unit})` }))}
                                placeholder="Select Food"
                                fallbackLabel={item.food}
                              />
                            </div>
                            <div className="col-span-2">
                              <label className="block text-[10px] uppercase tracking-wider text-gray-400 font-bold mb-1">Qty ({item.unit || 'g'})</label>
                              <input
                                type="number"
                                value={item.qty}
                                onChange={(e) => updateMealItem(meal.id, item.id, 'qty', e.target.value)}
                                placeholder="Value"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-black focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                            <div className="col-span-4 grid grid-cols-4 gap-1">
                              <div className="text-center">
                                <label className="block text-[8px] text-gray-400 font-bold uppercase">Cal</label>
                                <div className="text-xs font-semibold text-blue-700">{item.caloriesInKcal || 0}</div>
                              </div>
                              <div className="text-center">
                                <label className="block text-[8px] text-gray-400 font-bold uppercase">Pro</label>
                                <div className="text-xs font-semibold text-green-700">{item.proteinInGrams || 0}</div>
                              </div>
                              <div className="text-center">
                                <label className="block text-[8px] text-gray-400 font-bold uppercase">Car</label>
                                <div className="text-xs font-semibold text-yellow-700">{item.carbsInGrams || 0}</div>
                              </div>
                              <div className="text-center">
                                <label className="block text-[8px] text-gray-400 font-bold uppercase">Fat</label>
                                <div className="text-xs font-semibold text-purple-700">{item.fatInGrams || 0}</div>
                              </div>
                            </div>
                            <div className="col-span-1 flex justify-end">
                              <button
                                type="button"
                                onClick={() => removeMealItem(meal.id, item.id)}
                                className="p-2 text-gray-400 hover:text-red-500 rounded-md"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))}

                        {meal.items.length === 0 && (
                          <div className="text-center py-4 border-2 border-dashed border-gray-200 rounded-lg">
                            <button
                              type="button"
                              onClick={() => addMealItem(meal.id)}
                              className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center justify-center gap-1 mx-auto"
                            >
                              <Plus className="w-4 h-4" /> Add your first food item
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Nutritional Summary Progress Bars */}
                <div className="mt-8 bg-white rounded-xl p-5 border border-blue-200 shadow-sm">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-blue-600" />
                      Assigned Macros Progress
                    </h3>
                    <div className="text-xs text-gray-500 italic">Target based on fields above</div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                    {/* Calories */}
                    <div className="space-y-1.5">
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-600 font-semibold">Calories (kcal)</span>
                        <span className={currentTotals.calories > formData.caloriesPerDay ? 'text-red-600 font-bold' : 'text-blue-700 font-bold'}>
                          {currentTotals.calories} / {formData.caloriesPerDay || 0}
                        </span>
                      </div>
                      <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden border border-gray-200">
                        <div
                          className={`h-full transition-all duration-700 ease-out ${currentTotals.calories > formData.caloriesPerDay ? 'bg-red-500' : 'bg-blue-500'}`}
                          style={{ width: `${Math.min(100, (currentTotals.calories / (formData.caloriesPerDay || 1)) * 100)}%` }}
                        />
                      </div>
                    </div>
                    {formData.showMacros && (
                      <div className="space-y-1.5">
                        <div className="flex justify-between text-xs">
                          <span className="text-gray-600 font-semibold">Protein (g)</span>
                          <span className={currentTotals.protein > formData.proteinGramsPerDay ? 'text-red-600 font-bold' : 'text-green-700 font-bold'}>
                            {currentTotals.protein} / {formData.proteinGramsPerDay || 0}
                          </span>
                        </div>
                        <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden border border-gray-200">
                          <div
                            className={`h-full transition-all duration-700 ease-out ${currentTotals.protein > formData.proteinGramsPerDay ? 'bg-red-500' : 'bg-green-500'}`}
                            style={{ width: `${Math.min(100, (currentTotals.protein / (formData.proteinGramsPerDay || 1)) * 100)}%` }}
                          />
                        </div>
                      </div>
                    )}
                    {formData.showMacros && (
                      <div className="space-y-1.5">
                        <div className="flex justify-between text-xs">
                          <span className="text-gray-600 font-semibold">Carbs (g)</span>
                          <span className={currentTotals.carbs > formData.carbsGramsPerDay ? 'text-red-600 font-bold' : 'text-yellow-700 font-bold'}>
                            {currentTotals.carbs} / {formData.carbsGramsPerDay || 0}
                          </span>
                        </div>
                        <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden border border-gray-200">
                          <div
                            className={`h-full transition-all duration-700 ease-out ${currentTotals.carbs > formData.carbsGramsPerDay ? 'bg-red-500' : 'bg-yellow-500'}`}
                            style={{ width: `${Math.min(100, (currentTotals.carbs / (formData.carbsGramsPerDay || 1)) * 100)}%` }}
                          />
                        </div>
                      </div>
                    )}
                    {formData.showMacros && (
                      <div className="space-y-1.5">
                        <div className="flex justify-between text-xs">
                          <span className="text-gray-600 font-semibold">Fats (g)</span>
                          <span className={currentTotals.fat > formData.fatsGramsPerDay ? 'text-red-600 font-bold' : 'text-purple-700 font-bold'}>
                            {currentTotals.fat} / {formData.fatsGramsPerDay || 0}
                          </span>
                        </div>
                        <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden border border-gray-200">
                          <div
                            className={`h-full transition-all duration-700 ease-out ${currentTotals.fat > formData.fatsGramsPerDay ? 'bg-red-500' : 'bg-purple-500'}`}
                            style={{ width: `${Math.min(100, (currentTotals.fat / (formData.fatsGramsPerDay || 1)) * 100)}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Meal Plan Validation Error */}
              {formErrors.mealPlan && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-red-800">Meal Plan Validation Error</p>
                    <p className="text-sm text-red-700 mt-1">{formErrors.mealPlan}</p>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-800">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Enter description"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-800">Tags</label>
                <input
                  type="text"
                  value={formData.tags}
                  onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  placeholder="Enter tags (comma separated)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="mr-2"
                />
                <label htmlFor="isActive" className="text-sm font-medium text-gray-800">Active</label>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setShowCreateDialog(false)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={!formData.dietChartName || !formData.mealsPerDay || mealPlanEntries.some(meal => !meal.mealType || meal.mealType.trim() === '' || (meal.mealType === 'Custom' && (!meal.customMealType || meal.customMealType.trim() === '')))}
                className={`px-4 py-2 rounded-lg transition-all duration-200 ${!formData.dietChartName || !formData.mealsPerDay || mealPlanEntries.some(meal => !meal.mealType || meal.mealType.trim() === '' || (meal.mealType === 'Custom' && (!meal.customMealType || meal.customMealType.trim() === '')))
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-green-600 text-white hover:bg-green-700 shadow-sm hover:shadow-md'
                  }`}
                title={!formData.dietChartName || !formData.mealsPerDay || mealPlanEntries.some(meal => !meal.mealType || meal.mealType.trim() === '' || (meal.mealType === 'Custom' && (!meal.customMealType || meal.customMealType.trim() === ''))) ? 'Please fill in all required fields and ensure all meal groups have a meal type' : 'Create diet chart'}
              >
                {!formData.dietChartName || !formData.mealsPerDay || mealPlanEntries.some(meal => !meal.mealType || meal.mealType.trim() === '' || (meal.mealType === 'Custom' && (!meal.customMealType || meal.customMealType.trim() === ''))) ? (
                  <span className="flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    Complete Required Fields
                  </span>
                ) : (
                  'Create'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {showEditDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-black">Edit Diet Chart</h2>
              <button onClick={() => {
                resetForm();
                setShowEditDialog(false);
              }} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-800">Diet Chart Name *</label>
                  <input
                    type="text"
                    value={formData.dietChartName}
                    onChange={(e) => {
                      setFormData({ ...formData, dietChartName: e.target.value });
                      setFormErrors({ ...formErrors, dietChartName: '' });
                    }}
                    placeholder="Enter diet chart name"
                    className={`w-full px-3 py-2 border ${formErrors.dietChartName ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black`}
                  />
                  {formErrors.dietChartName && <p className="text-red-500 text-xs mt-1">{formErrors.dietChartName}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-800">Diet Type *</label>
                  <select
                    value={formData.dietType}
                    onChange={(e) => {
                      setFormData({ ...formData, dietType: e.target.value });
                      setFormErrors({ ...formErrors, dietType: '' });
                    }}
                    className={`w-full px-3 py-2 border ${formErrors.dietType ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black`}
                  >
                    <option value="VEGETARIAN">Vegetarian</option>
                    <option value="NON_VEGETARIAN">Non-Vegetarian</option>
                  </select>
                  {formErrors.dietType && <p className="text-red-500 text-xs mt-1">{formErrors.dietType}</p>}
                </div>

              </div>

              <div className="grid grid-cols-2 gap-4 pb-4 border-b border-gray-100">


                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-800">Calories/Day *</label>
                  <input
                    type="text"
                    value={formData.caloriesPerDay}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        caloriesPerDay: e.target.value === '' ? '' : parseInt(e.target.value) || '',
                      });
                      setFormErrors({ ...formErrors, caloriesPerDay: '' });
                    }}
                    placeholder="e.g., 2000"
                    className={`w-full px-3 py-2 border ${formErrors.caloriesPerDay ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black`}
                  />
                  {formErrors.caloriesPerDay && <p className="text-red-500 text-xs mt-1">{formErrors.caloriesPerDay}</p>}
                </div>

                <div className="flex items-center mt-6">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.showMacros}
                      onChange={(e) => setFormData({ ...formData, showMacros: e.target.checked })}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm font-medium text-gray-800">Enter macro nutrients</span>
                  </label>
                </div>
              </div>

              {formData.showMacros && (
                <div className="grid grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-800">Protein (g)</label>
                    <input
                      type="text"
                      value={formData.proteinGramsPerDay}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          proteinGramsPerDay: e.target.value === '' ? '' : parseInt(e.target.value) || '',
                        })
                      }
                      placeholder="e.g., 150"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-800">Carbs (g)</label>
                    <input
                      type="text"
                      value={formData.carbsGramsPerDay}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          carbsGramsPerDay: e.target.value === '' ? '' : parseInt(e.target.value) || '',
                        })
                      }
                      placeholder="e.g., 250"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-800">Fats (g)</label>
                    <input
                      type="text"
                      value={formData.fatsGramsPerDay}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          fatsGramsPerDay: e.target.value === '' ? '' : parseInt(e.target.value) || '',
                        })
                      }
                      placeholder="e.g., 100"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black"
                    />
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-800">Meals Per Day *</label>
                  <select
                    value={formData.mealsPerDay}
                    onChange={(e) => {
                      setFormData({ ...formData, mealsPerDay: parseInt(e.target.value) });
                      setFormErrors({ ...formErrors, mealsPerDay: '' });
                    }}
                    className={`w-full px-3 py-2 border ${formErrors.mealsPerDay ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black`}
                  >
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                      <option key={num} value={num}>{num} meal{num > 1 ? 's' : ''} per day</option>
                    ))}
                  </select>
                  {formErrors.mealsPerDay && <p className="text-red-500 text-xs mt-1">{formErrors.mealsPerDay}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-800">Duration</label>
                  <input
                    type="text"
                    value={formData.duration}
                    onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                    placeholder="e.g., 30 days"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-800">Price (Optional)</label>
                  <input
                    type="text"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        price: e.target.value === '' ? '' : parseFloat(e.target.value) || '',
                      });
                    }}
                    placeholder="e.g., 999.00 (leave empty if free)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black"
                  />
                </div>

              </div>


              {/* Meal Plan Section - Enhanced */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
                <div className="flex justify-between items-center mb-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Utensils className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Meal Plan Design</h3>
                      <p className="text-sm text-gray-600">Assign specific food items and quantities per meal</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={addMealPlanEntry}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center gap-2 shadow-sm hover:shadow-md"
                  >
                    <Plus className="w-4 h-4" />
                    Add Meal Group
                  </button>
                </div>

                <div className="space-y-6">
                  {mealPlanEntries.map((meal) => (
                    <div key={meal.id} className="bg-white rounded-xl border border-gray-200 shadow-sm">
                      <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex justify-between items-start rounded-t-xl">
                        <div className="flex-1 flex items-start gap-4">
                          <div className="flex-1">
                            <label className="block text-xs font-medium text-gray-600 mb-1">Meal Type *</label>
                            <div className="space-y-2">
                              <select
                                value={meal.mealType}
                                onChange={(e) => {
                                  updateMealType(meal.id, e.target.value);
                                  // Clear meal plan error when user selects a meal type
                                  if (formErrors.mealPlan) {
                                    setFormErrors({ ...formErrors, mealPlan: '' });
                                  }
                                }}
                                className={`w-full px-3 py-2 border ${!meal.mealType ? 'border-red-300 bg-red-50' : 'border-gray-300 bg-white'} rounded-lg text-gray-800 font-semibold text-base focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                              >
                                <option value="" disabled>Select meal type</option>
                                <option value="Breakfast">🍳 Breakfast</option>
                                <option value="Lunch">🍽️ Lunch</option>
                                <option value="Dinner">🌙 Dinner</option>
                                <option value="Snacks">🍿 Snacks</option>
                                <option value="Pre-Workout">💪 Pre-Workout</option>
                                <option value="Post-Workout">🏋️ Post-Workout</option>
                                <option value="Custom">✏️ Custom</option>
                              </select>

                              {/* Custom meal type input */}
                              {meal.mealType === 'Custom' && (
                                <input
                                  type="text"
                                  value={meal.customMealType}
                                  onChange={(e) => {
                                    updateCustomMealType(meal.id, e.target.value);
                                    // Clear meal plan error when user enters custom name
                                    if (formErrors.mealPlan) {
                                      setFormErrors({ ...formErrors, mealPlan: '' });
                                    }
                                  }}
                                  placeholder="Enter custom meal type name"
                                  className={`w-full px-3 py-2 border ${meal.mealType === 'Custom' && !meal.customMealType ? 'border-red-300 bg-red-50' : 'border-gray-300 bg-white'} rounded-lg text-gray-800 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                                />
                              )}
                            </div>
                            {!meal.mealType && (
                              <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                                <AlertCircle className="w-3 h-3" />
                                Please select a meal type
                              </p>
                            )}
                            {meal.mealType === 'Custom' && !meal.customMealType && (
                              <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                                <AlertCircle className="w-3 h-3" />
                                Please enter a custom meal type name
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-start gap-2 ml-4 pt-5">
                          <button
                            type="button"
                            onClick={() => addMealItem(meal.id)}
                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                            title="Add food item"
                          >
                            <Plus className="w-5 h-5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => removeMealPlanEntry(meal.id)}
                            className="p-1.5 text-red-500 hover:bg-red-50 rounded-md transition-colors"
                            title="Remove meal group"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </div>

                      <div className="p-4 space-y-3">
                        {meal.items.map((item) => (
                          <div key={item.id} className="grid grid-cols-12 gap-3 items-end bg-gray-50 p-3 rounded-lg border border-gray-100 relative group">
                            <div className="col-span-5">
                              <label className="block text-[10px] uppercase tracking-wider text-gray-400 font-bold mb-1">Food Item</label>
                              <SearchableSelect
                                value={item.foodMacroValue}
                                onChange={(val) => updateMealItem(meal.id, item.id, 'foodMacroValue', val)}
                                options={allMacros.map(m => ({ value: m.id, label: `${m.food} (${m.qty} ${m.unit})` }))}
                                placeholder="Select Food"
                                fallbackLabel={item.food}
                              />
                            </div>
                            <div className="col-span-2">
                              <label className="block text-[10px] uppercase tracking-wider text-gray-400 font-bold mb-1">Qty ({item.unit || 'g'})</label>
                              <input
                                type="number"
                                value={item.qty}
                                onChange={(e) => updateMealItem(meal.id, item.id, 'qty', e.target.value)}
                                placeholder="Value"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-black focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                            <div className="col-span-4 grid grid-cols-4 gap-1">
                              <div className="text-center">
                                <label className="block text-[8px] text-gray-400 font-bold uppercase">Cal</label>
                                <div className="text-xs font-semibold text-blue-700">{item.caloriesInKcal || 0}</div>
                              </div>
                              <div className="text-center">
                                <label className="block text-[8px] text-gray-400 font-bold uppercase">Pro</label>
                                <div className="text-xs font-semibold text-green-700">{item.proteinInGrams || 0}</div>
                              </div>
                              <div className="text-center">
                                <label className="block text-[8px] text-gray-400 font-bold uppercase">Car</label>
                                <div className="text-xs font-semibold text-yellow-700">{item.carbsInGrams || 0}</div>
                              </div>
                              <div className="text-center">
                                <label className="block text-[8px] text-gray-400 font-bold uppercase">Fat</label>
                                <div className="text-xs font-semibold text-purple-700">{item.fatInGrams || 0}</div>
                              </div>
                            </div>
                            <div className="col-span-1 flex justify-end">
                              <button
                                type="button"
                                onClick={() => removeMealItem(meal.id, item.id)}
                                className="p-2 text-gray-400 hover:text-red-500 rounded-md"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))}

                        {meal.items.length === 0 && (
                          <div className="text-center py-4 border-2 border-dashed border-gray-200 rounded-lg">
                            <button
                              type="button"
                              onClick={() => addMealItem(meal.id)}
                              className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center justify-center gap-1 mx-auto"
                            >
                              <Plus className="w-4 h-4" /> Add your first food item
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Nutritional Summary Progress Bars */}
                <div className="mt-8 bg-white rounded-xl p-5 border border-blue-200 shadow-sm">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-blue-600" />
                      Assigned Macros Progress
                    </h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                    {/* Calories */}
                    <div className="space-y-1.5">
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-600 font-semibold">Calories (kcal)</span>
                        <span className={currentTotals.calories > formData.caloriesPerDay ? 'text-red-600 font-bold' : 'text-blue-700 font-bold'}>
                          {currentTotals.calories} / {formData.caloriesPerDay || 0}
                        </span>
                      </div>
                      <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden border border-gray-200">
                        <div
                          className={`h-full transition-all duration-700 ease-out ${currentTotals.calories > formData.caloriesPerDay ? 'bg-red-500' : 'bg-blue-500'}`}
                          style={{ width: `${Math.min(100, (currentTotals.calories / (formData.caloriesPerDay || 1)) * 100)}%` }}
                        />
                      </div>
                    </div>
                    {formData.showMacros && (
                      <>
                        {/* Protein */}
                        <div className="space-y-1.5">
                          <div className="flex justify-between text-xs">
                            <span className="text-gray-600 font-semibold">Protein (g)</span>
                            <span className={currentTotals.protein > formData.proteinGramsPerDay ? 'text-red-600 font-bold' : 'text-green-700 font-bold'}>
                              {currentTotals.protein} / {formData.proteinGramsPerDay || 0}
                            </span>
                          </div>
                          <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden border border-gray-200">
                            <div
                              className={`h-full transition-all duration-700 ease-out ${currentTotals.protein > formData.proteinGramsPerDay ? 'bg-red-500' : 'bg-green-500'}`}
                              style={{ width: `${Math.min(100, (currentTotals.protein / (formData.proteinGramsPerDay || 1)) * 100)}%` }}
                            />
                          </div>
                        </div>
                        {/* Carbs */}
                        <div className="space-y-1.5">
                          <div className="flex justify-between text-xs">
                            <span className="text-gray-600 font-semibold">Carbs (g)</span>
                            <span className={currentTotals.carbs > formData.carbsGramsPerDay ? 'text-red-600 font-bold' : 'text-yellow-700 font-bold'}>
                              {currentTotals.carbs} / {formData.carbsGramsPerDay || 0}
                            </span>
                          </div>
                          <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden border border-gray-200">
                            <div
                              className={`h-full transition-all duration-700 ease-out ${currentTotals.carbs > formData.carbsGramsPerDay ? 'bg-red-500' : 'bg-yellow-500'}`}
                              style={{ width: `${Math.min(100, (currentTotals.carbs / (formData.carbsGramsPerDay || 1)) * 100)}%` }}
                            />
                          </div>
                        </div>
                        {/* Fat */}
                        <div className="space-y-1.5">
                          <div className="flex justify-between text-xs">
                            <span className="text-gray-600 font-semibold">Fats (g)</span>
                            <span className={currentTotals.fat > formData.fatsGramsPerDay ? 'text-red-600 font-bold' : 'text-purple-700 font-bold'}>
                              {currentTotals.fat} / {formData.fatsGramsPerDay || 0}
                            </span>
                          </div>
                          <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden border border-gray-200">
                            <div
                              className={`h-full transition-all duration-700 ease-out ${currentTotals.fat > formData.fatsGramsPerDay ? 'bg-red-500' : 'bg-purple-500'}`}
                              style={{ width: `${Math.min(100, (currentTotals.fat / (formData.fatsGramsPerDay || 1)) * 100)}%` }}
                            />
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Meal Plan Validation Error */}
              {formErrors.mealPlan && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-red-800">Meal Plan Validation Error</p>
                    <p className="text-sm text-red-700 mt-1">{formErrors.mealPlan}</p>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-800">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Enter description"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-800">Tags</label>
                <input
                  type="text"
                  value={formData.tags}
                  onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  placeholder="Enter tags (comma separated)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isActiveEdit"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="mr-2"
                />
                <label htmlFor="isActiveEdit" className="text-sm font-medium text-gray-800">Active</label>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => {
                  resetForm();
                  setShowEditDialog(false);
                }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdate}
                disabled={!formData.dietChartName || !formData.mealsPerDay || mealPlanEntries.some(meal => !meal.mealType || meal.mealType.trim() === '' || (meal.mealType === 'Custom' && (!meal.customMealType || meal.customMealType.trim() === '')))}
                className={`px-4 py-2 rounded-lg transition-all duration-200 ${!formData.dietChartName || !formData.mealsPerDay || mealPlanEntries.some(meal => !meal.mealType || meal.mealType.trim() === '' || (meal.mealType === 'Custom' && (!meal.customMealType || meal.customMealType.trim() === '')))
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-green-600 text-white hover:bg-green-700 shadow-sm hover:shadow-md'
                  }`}
                title={!formData.dietChartName || !formData.mealsPerDay || mealPlanEntries.some(meal => !meal.mealType || meal.mealType.trim() === '' || (meal.mealType === 'Custom' && (!meal.customMealType || meal.customMealType.trim() === ''))) ? 'Please fill in all required fields and ensure all meal groups have a meal type' : 'Update diet chart'}
              >
                {!formData.dietChartName || !formData.mealsPerDay || mealPlanEntries.some(meal => !meal.mealType || meal.mealType.trim() === '' || (meal.mealType === 'Custom' && (!meal.customMealType || meal.customMealType.trim() === ''))) ? (
                  <span className="flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    Complete Required Fields
                  </span>
                ) : (
                  'Update'
                )}
              </button>
            </div>
          </div>
        </div>
      )}


      {showViewDialog && selectedDietChart && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 transition-opacity duration-300">
          <div className="bg-white rounded-xl w-full max-w-5xl max-h-[85vh] overflow-hidden shadow-2xl flex flex-col animate-in fade-in zoom-in-95 duration-200">

            {/* Header */}
            <div className="px-5 py-3 border-b border-gray-100 flex justify-between items-center bg-white z-10">
              <div className="flex items-center gap-3">
                <h2 className="text-lg font-bold text-gray-900 truncate max-w-md">
                  {selectedDietChart.dietChartName}
                </h2>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${selectedDietChart.isActive
                  ? 'bg-emerald-50 text-emerald-700 border border-emerald-100'
                  : 'bg-rose-50 text-rose-700 border border-rose-100'
                  }`}>
                  {selectedDietChart.isActive ? 'Active' : 'Inactive'}
                </span>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${selectedDietChart.dietType === 'NON_VEGETARIAN'
                  ? 'bg-red-50 text-red-700 border border-red-100'
                  : 'bg-green-50 text-green-700 border border-green-100'
                  }`}>
                  {selectedDietChart.dietType === 'NON_VEGETARIAN' ? 'Non-Vegetarian' : 'Vegetarian'}
                </span>
              </div>
              <button
                onClick={() => setShowViewDialog(false)}
                className="p-1.5 bg-gray-50 hover:bg-gray-100 rounded-full transition-colors text-gray-400 hover:text-gray-600 focus:outline-none"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Main Content - Single Scroller */}
            <div className="flex-1 overflow-y-auto custom-scrollbar bg-gray-50/50">
              <div className="flex flex-col md:flex-row min-h-full">

                {/* Left Sidebar: Meta Info */}
                <div className="w-full md:w-[320px] bg-gray-50/80 border-r border-gray-100 p-5 space-y-5 h-full">

                  {/* Image */}
                  {selectedDietChart.imageUrl && (
                    <div className="rounded-lg overflow-hidden border border-gray-200 shadow-sm aspect-video bg-white">
                      <img
                        src={selectedDietChart.imageUrl}
                        alt={selectedDietChart.dietChartName}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}

                  {/* Macro Summary - Compact Grid */}
                  <div>
                    <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                      <Activity className="w-3 h-3" /> Daily Targets
                    </h3>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="bg-white p-2.5 rounded-lg border border-gray-100 shadow-sm">
                        <div className="text-[10px] text-gray-500 font-medium uppercase">Calories</div>
                        <div className="text-lg font-bold text-gray-900 leading-tight">{selectedDietChart.caloriesPerDay}</div>
                        <div className="text-[10px] text-gray-400">kcal</div>
                      </div>
                      {selectedDietChart.showMacros && (
                        <>
                          <div className="bg-white p-2.5 rounded-lg border border-gray-100 shadow-sm">
                            <div className="text-[10px] text-gray-500 font-medium uppercase">Protein</div>
                            <div className="text-lg font-bold text-emerald-600 leading-tight">{selectedDietChart.proteinGramsPerDay}g</div>
                            <div className="text-[10px] text-gray-400">grams</div>
                          </div>
                          <div className="bg-white p-2.5 rounded-lg border border-gray-100 shadow-sm">
                            <div className="text-[10px] text-gray-500 font-medium uppercase">Carbs</div>
                            <div className="text-lg font-bold text-amber-600 leading-tight">{selectedDietChart.carbsGramsPerDay}g</div>
                            <div className="text-[10px] text-gray-400">grams</div>
                          </div>
                          <div className="bg-white p-2.5 rounded-lg border border-gray-100 shadow-sm">
                            <div className="text-[10px] text-gray-500 font-medium uppercase">Fats</div>
                            <div className="text-lg font-bold text-purple-600 leading-tight">{selectedDietChart.fatsGramsPerDay}g</div>
                            <div className="text-[10px] text-gray-400">grams</div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Details List */}
                  <div>
                    <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                      <Info className="w-3 h-3" /> Details
                    </h3>
                    <div className="bg-white rounded-lg border border-gray-100 shadow-sm divide-y divide-gray-50">

                      <div className="flex justify-between items-center p-3 text-sm">
                        <span className="text-gray-500">Duration</span>
                        <span className="font-medium text-gray-900">{selectedDietChart.duration ? `${selectedDietChart.duration} Days` : '-'}</span>
                      </div>
                      <div className="flex justify-between items-center p-3 text-sm">
                        <span className="text-gray-500">Frequency</span>
                        <span className="font-medium text-gray-900">{selectedDietChart.mealsPerDay || ""} Meals/Day</span>
                      </div>
                      <div className="flex justify-between items-center p-3 text-sm">
                        <span className="text-gray-500">Price</span>
                        <span className="font-bold text-gray-900">{selectedDietChart.price ? `₹${selectedDietChart.price}` : 'Free'}</span>
                      </div>

                    </div>
                  </div>

                  {/* Description */}
                  {(selectedDietChart.description || selectedDietChart.tags) && (
                    <div>
                      <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-2 flex items-center gap-2">
                        <FileText className="w-3 h-3" /> About
                      </h3>
                      {selectedDietChart.description && (
                        <p className="text-sm text-gray-600 leading-relaxed mb-3">
                          {selectedDietChart.description}
                        </p>
                      )}
                      {selectedDietChart.tags && (
                        <div className="flex flex-wrap gap-1.5">
                          {selectedDietChart.tags.split(',').map((tag, i) => (
                            <span key={i} className="px-2 py-0.5 bg-white text-gray-600 text-[10px] font-medium rounded border border-gray-200 shadow-sm">
                              {tag.trim()}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Right Content: Meal Plan */}
                <div className="flex-1 bg-white p-5 h-full">
                  <div className="flex items-center justify-between mb-4 pb-2 border-b border-gray-50">
                    <h3 className="font-bold text-gray-900 flex items-center gap-2">
                      <Utensils className="w-4 h-4 text-blue-500" /> Meal Schedule
                    </h3>
                  </div>

                  <div className="space-y-4">
                    {(() => {
                      const mealOrder = ['Breakfast', 'Lunch', 'Dinner', 'Snacks', 'Pre-Workout', 'Post-Workout', 'Custom'];
                      const grouped = {};
                      (selectedDietChart.mealPlan || []).forEach(item => {
                        const type = item.mealType || 'Other';
                        if (!grouped[type]) grouped[type] = [];
                        grouped[type].push(item);
                      });

                      const sortedKeys = Object.keys(grouped).sort((a, b) => {
                        const idxA = mealOrder.indexOf(a);
                        const idxB = mealOrder.indexOf(b);
                        if (idxA === -1 && idxB === -1) return a.localeCompare(b);
                        if (idxA === -1) return 1;
                        if (idxB === -1) return -1;
                        return idxA - idxB;
                      });

                      if (sortedKeys.length === 0) {
                        return (
                          <div className="text-center py-12 text-gray-400 bg-gray-50 rounded-lg border border-dashed border-gray-200">
                            <Utensils className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                            <p>No meals added to this plan.</p>
                          </div>
                        );
                      }

                      return sortedKeys.map(type => (
                        <div key={type} className="border border-gray-100 rounded-lg overflow-hidden">
                          <div className="bg-gray-50/50 px-4 py-2 border-b border-gray-100 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="text-lg">
                                {type === 'Breakfast' ? '🍳' :
                                  type === 'Lunch' ? '🍽️' :
                                    type === 'Dinner' ? '🌙' :
                                      type === 'Snacks' ? '🍿' :
                                        type === 'Pre-Workout' ? '💪' :
                                          type === 'Post-Workout' ? '🏋️' : '🍲'}
                              </span>
                              <h4 className="font-semibold text-gray-800 text-sm">{type}</h4>
                            </div>
                            <span className="text-[10px] font-bold text-gray-400 bg-white px-2 py-0.5 rounded border border-gray-100 shadow-sm">
                              {grouped[type].length} Items
                            </span>
                          </div>

                          <div className="divide-y divide-gray-50">
                            {grouped[type].map((item, idx) => (
                              <div key={idx} className="px-4 py-3 hover:bg-gray-50 transition-colors">
                                <div className="flex items-start justify-between gap-3">
                                  <div className="flex-1">
                                    <div className="flex items-baseline justify-between mb-1">
                                      <span className="text-sm font-semibold text-gray-800">{item.food || (item.remarks && !isNaN(item.remarks) ? 'Unnamed Food Item' : item.remarks) || 'Unnamed Food Item'}</span>
                                      <span className="text-xs font-bold text-gray-600 bg-gray-100 px-2 py-0.5 rounded ml-2 whitespace-nowrap">
                                        {item.qty} {item.unit || 'g'}
                                      </span>
                                    </div>

                                    {item.remarks && (
                                      <p className="text-xs text-gray-500 italic mb-2 flex items-start gap-1">
                                        <Info className="w-3 h-3 mt-0.5 flex-shrink-0 text-blue-400" />
                                        {item.remarks}
                                      </p>
                                    )}

                                    <div className="flex items-center gap-3 mt-1.5">
                                      <div className="flex items-center gap-1" title="Calories">
                                        <div className="w-1.5 h-1.5 rounded-full bg-blue-400"></div>
                                        <span className="text-[10px] font-medium text-gray-600">{item.caloriesInKcal} cal</span>
                                      </div>
                                      <div className="flex items-center gap-1" title="Protein">
                                        <div className="w-1.5 h-1.5 rounded-full bg-emerald-400"></div>
                                        <span className="text-[10px] font-medium text-gray-600">{item.proteinInGrams}p</span>
                                      </div>
                                      <div className="flex items-center gap-1" title="Carbs">
                                        <div className="w-1.5 h-1.5 rounded-full bg-amber-400"></div>
                                        <span className="text-[10px] font-medium text-gray-600">{item.carbsInGrams}c</span>
                                      </div>
                                      <div className="flex items-center gap-1" title="Fats">
                                        <div className="w-1.5 h-1.5 rounded-full bg-purple-400"></div>
                                        <span className="text-[10px] font-medium text-gray-600">{item.fatInGrams}f</span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-5 py-3 border-t border-gray-100 bg-gray-50/50 flex justify-end gap-3 z-10">
              <button
                onClick={() => setShowViewDialog(false)}
                className="px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 font-medium text-sm shadow-sm transition-all"
              >
                Close
              </button>
              <button
                onClick={() => {
                  setShowViewDialog(false);
                  openEditDialog(selectedDietChart);
                }}
                className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-black font-medium text-sm shadow-md transition-all flex items-center gap-2"
              >
                <Edit className="w-3.5 h-3.5" />
                Edit Plan
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Assign Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">Assign Diet Chart</h2>
              <button onClick={() => { setShowAssignModal(false); resetAssignmentForm(); }} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-800">Assignment Mode</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setAssignMode('global')}
                    className={`px-3 py-2 rounded border ${assignMode === 'global' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-800 border-gray-300'}`}
                  >
                    Global
                  </button>
                  <button
                    onClick={() => setAssignMode('selective')}
                    className={`px-3 py-2 rounded border ${assignMode === 'selective' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-800 border-gray-300'}`}
                  >
                    Selective
                  </button>
                  <button
                    onClick={() => setAssignMode('subscription')}
                    className={`px-3 py-2 rounded border ${assignMode === 'subscription' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-800 border-gray-300'}`}
                  >
                    By Subscription
                  </button>
                  <button
                    onClick={() => setAssignMode('addon')}
                    className={`px-3 py-2 rounded border ${assignMode === 'addon' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-800 border-gray-300'}`}
                  >
                    By Addon
                  </button>
                </div>
              </div>

              {(assignMode === 'subscription' || assignMode === 'addon') && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-800">Plan Type</label>
                    <select
                      value={assignMode === 'subscription' ? 'subscription' : 'addon'}
                      onChange={(e) => {
                        setPlanType(e.target.value);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black"
                      disabled
                    >
                      <option value="subscription">Subscription Plan</option>
                      <option value="addon">Addon Plan</option>
                    </select>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-800">Select Plan</label>
                      <button
                        type="button"
                        onClick={fetchPlans}
                        disabled={plansLoading}
                        className="text-xs text-blue-600 hover:text-blue-800 disabled:opacity-50"
                        title="Refresh plans"
                      >
                        <RefreshCw className={`w-3 h-3 ${plansLoading ? 'animate-spin' : ''}`} />
                      </button>
                    </div>
                    <SearchableSelect
                      options={assignMode === 'subscription'
                        ? subscriptionPlans.map(p => ({ label: `${p.planName} - ₹${p.price} (${p.timePeriod})`, value: p.id }))
                        : addonPlans.map(p => ({ label: `${p.planName} - ₹${p.price}`, value: p.id }))
                      }
                      value={assignMode === 'subscription' ? selectedSubscriptionPlanId : selectedAddonPlanId}
                      onChange={(val) => assignMode === 'subscription' ? setSelectedSubscriptionPlanId(val) : setSelectedAddonPlanId(val)}
                      placeholder={plansLoading ? "Loading plans..." : "Select a plan..."}
                      disabled={plansLoading}
                      emptyText={assignMode === 'subscription' ? "No subscription plans available" : "No addon plans available"}
                    />
                    {plansLoading && (
                      <p className="text-xs text-gray-500 mt-1">Loading available plans...</p>
                    )}
                    {!plansLoading && assignMode === 'subscription' && subscriptionPlans.length === 0 && (
                      <p className="text-xs text-red-500 mt-1">No subscription plans available</p>
                    )}
                    {!plansLoading && assignMode === 'addon' && addonPlans.length === 0 && (
                      <p className="text-xs text-red-500 mt-1">No addon plans available</p>
                    )}
                    {(assignMode === 'subscription' ? selectedSubscriptionPlanId : selectedAddonPlanId) && (
                      <div className="mt-2 p-2 bg-blue-50 rounded border border-blue-200">
                        <p className="text-xs text-blue-800">
                          <strong>Selected Plan:</strong> {
                            assignMode === 'subscription'
                              ? subscriptionPlans.find(p => p.id == selectedSubscriptionPlanId)?.planName
                              : addonPlans.find(p => p.id == selectedAddonPlanId)?.planName
                          }
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {assignMode === 'selective' && (
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-800">Select Users</label>
                  <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-lg p-2">
                    {users.map((user) => (
                      <label key={user.value} className="flex items-center space-x-2 p-1 hover:bg-gray-50">
                        <input
                          type="checkbox"
                          checked={selectedUserIds.includes(user.value)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedUserIds([...selectedUserIds, user.value]);
                            } else {
                              setSelectedUserIds(selectedUserIds.filter(id => id !== user.value));
                            }
                          }}
                          className="rounded"
                        />
                        <span className="text-sm text-gray-800">
                          {user.firstName} {user.lastName} ({user.email})
                        </span>
                      </label>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Selected: {selectedUserIds.length} users
                  </p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-800">Assigned By</label>
                  <input
                    type="text"
                    value={assignedBy}
                    onChange={(e) => setAssignedBy(e.target.value)}
                    placeholder="admin"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-800">Reason</label>
                  <input
                    type="text"
                    value={assignReason}
                    onChange={(e) => setAssignReason(e.target.value)}
                    placeholder="Reason for assignment"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => { setShowAssignModal(false); resetAssignmentForm(); }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAssignDietChart}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                Assign
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Confirm Modal */}
      {confirmModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4 backdrop-blur-sm transition-all duration-300">
          <div className="bg-white rounded-2xl max-w-sm w-full shadow-2xl transform transition-all duration-300 scale-100 p-6 border border-gray-100">
            <div className="flex flex-col items-center text-center">
              <div className="bg-red-50 p-4 rounded-full mb-4">
                <AlertTriangle className="w-8 h-8 text-red-500" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {confirmModal.title}
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed mb-8">
                {confirmModal.message}
              </p>
              <div className="flex gap-3 w-full">
                <button
                  onClick={() => setConfirmModal(prev => ({ ...prev, show: false }))}
                  className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl transition-colors font-semibold text-sm focus:outline-none focus:ring-2 focus:ring-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmModal.onConfirm}
                  className="flex-1 px-4 py-2.5 bg-red-500 hover:bg-red-600 text-white rounded-xl shadow-lg shadow-red-200 transition-all duration-200 font-semibold text-sm focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-offset-2"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>
        {`
          @media print {
            body * { visibility: hidden; }
            .print-section, .print-section * { visibility: visible; }
            .print-section { 
              position: absolute; 
              left: 0; 
              top: 0; 
              width: 100%; 
              padding: 40px;
              background: white;
            }
            .no-print { display: none !important; }
          }
        `}
      </style>

      {/* Bulk Upload Instructions Modal */}
      {showBulkInstructions && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4 backdrop-blur-sm overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-2xl w-full shadow-2xl p-6 border border-gray-100 max-h-[90vh] overflow-y-auto print-section">
            <div className="flex justify-between items-center mb-6 no-print">
              <div className="flex items-center gap-3">
                <div className="bg-indigo-100 p-2 rounded-lg">
                  <Info className="w-6 h-6 text-indigo-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">Bulk Upload Instructions</h2>
              </div>
              <button
                onClick={() => setShowBulkInstructions(false)}
                className="text-gray-400 hover:text-gray-600 p-1 hover:bg-gray-100 rounded-lg transition-all"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="hidden print:block mb-8 text-center border-b pb-4">
              <h1 className="text-3xl font-bold text-indigo-700">Diet Chart Bulk Upload Guide</h1>
              <p className="text-gray-500 mt-2">Administrative Control Panel - BYB Cult</p>
            </div>

            <div className="space-y-6 text-sm text-gray-700">
              <section className="bg-yellow-50 p-4 rounded-xl border border-yellow-100">
                <h3 className="font-bold text-yellow-800 mb-2 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  Important Note for Data Integrity
                </h3>
                <p>Multiple rows are grouped into a single Diet Chart based on the <strong>Diet Chart Name</strong>. Ensure the name is identical for all meal items belonging to that chart.</p>
              </section>

              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="px-3 py-2 text-left font-bold text-gray-600">Column Name</th>
                      <th className="px-3 py-2 text-left font-bold text-gray-600">Accepted Values / Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {[
                      { col: 'Diet Chart Name', val: 'Required. Unique name used to group rows into one chart.' },
                      { col: 'Target Goal', val: 'Weight Loss, Weight Gain, Maintenance, etc.' },
                      { col: 'Calories Per Day', val: 'Numeric value (e.g., 2000)' },
                      { col: 'Diet Type', val: 'VEGETARIAN or NON_VEGETARIAN' },
                      { col: 'Show Macros', val: 'TRUE or FALSE' },
                      { col: 'Category', val: 'WEIGHT_LOSS, WEIGHT_GAIN, MUSCLE_BUILDING, MAINTENANCE, or ATHLETIC_PERFORMANCE' },
                      { col: 'Price', val: 'Numeric value (e.g., 999.00). Leave empty if chart is free.' },
                      { col: 'Meal Type', val: 'Breakfast, Lunch, Dinner, Snacks, Pre-Workout, or Custom' },
                      { col: 'Meal Item Data', val: 'Food name, quantity, unit, and nutritional macros per item.' },
                    ].map((row, i) => (
                      <tr key={i} className="border-b border-gray-50">
                        <td className="px-3 py-3 font-semibold text-indigo-700 align-top w-1/3">{row.col}</td>
                        <td className="px-3 py-3">{row.val}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex gap-4 no-print">
                <button
                  onClick={handleDownloadTemplate}
                  className="flex-1 flex items-center justify-center gap-2 py-3 bg-emerald-50 text-emerald-700 rounded-xl hover:bg-emerald-100 transition-colors font-bold border border-emerald-200"
                >
                  <Download className="w-5 h-5" />
                  CSV Template
                </button>
                <button
                  onClick={() => window.print()}
                  className="flex-1 flex items-center justify-center gap-2 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-all font-bold shadow-lg shadow-indigo-100"
                >
                  <Printer className="w-5 h-5" />
                  Save as PDF / Print
                </button>
              </div>

              <div className="no-print text-center pt-2">
                <button
                  onClick={handleDownloadInstructions}
                  className="text-xs text-gray-400 hover:text-indigo-500 underline"
                >
                  Download raw text guide (.txt)
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
